import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Spin, Typography, Row, Col } from 'antd';
import { LoadingOutlined, ArrowRightOutlined } from '@ant-design/icons';
import { Link } from 'react-router-dom';

import ReactEChartsCore from 'echarts-for-react/lib/core';
import { GaugeChart } from 'echarts/charts';
import * as echarts from 'echarts/core';
import { CanvasRenderer } from 'echarts/renderers';

import { getColor } from '../../helpers/utils';
import LicenseService from '../licenses/services';

echarts.use([GaugeChart, CanvasRenderer]);

const { Text } = Typography;

const GAUGE_HEIGHT = 180;

/**
 * Current month email usage: left = stats, right = semi-circular gauge with center label (Used / value).
 * Simple two-segment arc (used vs remaining), no axis labels.
 */
const EmailUsageCurrentMonthGauge = () => {
  const [currentMonth, setCurrentMonth] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await LicenseService.getEmailUsage();
      if (res?.data?.success && res?.data?.data?.current_month) {
        setCurrentMonth(res.data.data.current_month);
      } else {
        setCurrentMonth(null);
      }
    } catch (err) {
      console.error('Email usage current month:', err);
      setCurrentMonth(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const option = useMemo(() => {
    if (!currentMonth) return null;
    const { limit, used } = currentMonth;
    const hasLimit = limit != null && limit > 0;
    const maxVal = hasLimit ? limit : Math.max(used + 50, 100);
    const usedRatio = maxVal > 0 ? Math.min(1, used / maxVal) : 0;

    return {
      series: [{
        type: 'gauge',
        startAngle: 180,
        endAngle: 0,
        min: 0,
        max: maxVal,
        value: used,
        center: ['50%', '55%'],
        radius: '100%',
        axisLine: {
          lineStyle: {
            width: 20,
            color: [
              [usedRatio, getColor('primary')],
              [1, getColor('gray-200')],
            ],
          },
          roundCap: false,
        },
        axisTick: { show: false },
        splitLine: { show: false },
        axisLabel: { show: false },
        anchor: { show: false },
        title: { show: false },
        detail: { show: false },
      }],
    };
  }, [currentMonth]);

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: GAUGE_HEIGHT }}>
        <Spin indicator={<LoadingOutlined spin />} size="small" />
      </div>
    );
  }

  if (!currentMonth) {
    return (
      <div style={{ padding: 8, textAlign: 'center' }}>
        <Text type="secondary" style={{ fontSize: 12 }}>Email usage is not available.</Text>
      </div>
    );
  }

  const { limit, used, remaining, daily_average } = currentMonth;
  const hasLimit = limit != null && limit > 0;
  const percent = hasLimit && limit > 0 ? Math.round((used / limit) * 100) : null;

  return (
    <div className="email-usage-current-month-gauge">
      <Row gutter={[16, 16]} align="top">
        <Col xs={24} md={12}>
          <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
              <Text type="secondary">Limit:</Text>
              <Text strong>{hasLimit ? limit : '—'}</Text>
              {hasLimit && <Text type="secondary" style={{ fontSize: 11 }}>emails/month</Text>}
            </div>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, marginTop: 4 }}>
              <Text type="secondary">Used:</Text>
              <Text strong>{used}</Text>
              {percent != null && <Text type="secondary" style={{ fontSize: 12 }}>({percent}%)</Text>}
            </div>
            {hasLimit && remaining != null && (
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, marginTop: 4 }}>
                <Text type="secondary">Remaining:</Text>
                <Text strong>{remaining}</Text>
              </div>
            )}
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, marginTop: 4 }}>
              <Text type="secondary">Daily average:</Text>
              <Text strong>{daily_average}</Text>
            </div>
            <Link to="/licenses" style={{ marginTop: 8, display: 'inline-flex', alignItems: 'center', gap: 4}}>
              Manage email limit in Licenses
              <ArrowRightOutlined  />
            </Link>
          </div>
        </Col>
        <Col xs={24} md={12}>
          <div
            style={{
              minHeight: GAUGE_HEIGHT,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: getColor('gray-100'),
              borderRadius: 8,
              position: 'relative',
            }}
            className=''
          >
            <div className='' style={{ width: '100%', maxWidth: 200, height: GAUGE_HEIGHT, position: 'relative', overflow : 'hidden' }}>
              <div className='pt-4'>
                <ReactEChartsCore
                  echarts={echarts}
                  option={option}
                  style={{ height: GAUGE_HEIGHT, width: '100%' }}
                  opts={{ renderer: 'canvas' }}
                />
              </div>
              <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', paddingTop: '25%', pointerEvents: 'none' }}>
                <Text type="secondary" style={{ fontSize: 12, fontWeight: 600 }}>Used</Text>
                <Text strong style={{ fontSize: 22}}>{percent}<Text type="secondary" style={{ fontSize: 12 }}>%</Text></Text>
              </div>
            </div>
          </div>
        </Col>
      </Row>
    </div>
  );
};

export default EmailUsageCurrentMonthGauge;
